import React, { useState, useEffect } from "react";
import { companiesAPI, contactsAPI } from "../../../services/api";
import { toast } from "react-toastify";
import Select from 'react-select';
import { ChevronDown, Building2, Users, MapPin, CreditCard, Shield, X } from "lucide-react";

const SECTIONS = ["Company Info", "Lead Details", "Contact", "Address", "Tax & Payment"];

const CreateCompanyModal = ({ onClose, onCreate }) => {
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("Company Info");
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [sameAsBilling, setSameAsBilling] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  useEffect(() => {
    const fetchContacts = async () => {
      setContactsLoading(true);
      try {
        const response = await contactsAPI.getAllContacts();
        const formatted = (response.data || []).map(c => ({
          value: c.id,
          label: `${c.firstName} ${c.lastName}`,
          id: c.id
        }));
        setContacts(formatted);
      } catch (error) {
        console.error('Failed to fetch contacts:', error);
      } finally {
        setContactsLoading(false);
      }
    };
    fetchContacts();
  }, []);

  const [formData, setFormData] = useState({
    companyName: "",
    websiteUrl: "",
    linkedInUrl: "",
    industry: "",
    relationshipType: "",
    companyStage: "",
    legalType: "",
    employeeCount: "",
    annualRevenue: "",
    leadStatus: "",
    leadSource: "",
    primaryContact: "",
    contactIds: [],
    jobTitle: "",
    email: "",
    phone: "",
    doNotCall: false,
    doNotEmail: false,
    billingAddress: { streetLine1: "", streetLine2: "", city: "", state: "", country: "", zipcode: "" },
    shippingAddress: { streetLine1: "", streetLine2: "", city: "", state: "", country: "", zipcode: "" },
    sameAsBilling: false,
    taxIdType: "GST",
    taxIdNumber: "",
    paymentTerms: "",
    currency: "",
    isShared: false,
  });

  /* ===============================
     VALIDATION RULES
     =============================== */
  const validateField = (name, value) => {
    switch (name) {
      case "companyName": {
        if (!value.trim())             return "Company name is required.";
        if (value.trim().length < 2)   return "Company name must be at least 2 characters.";
        if (value.trim().length > 100) return "Company name must not exceed 100 characters.";
        return "";
      }
      case "email": {
        if (!value.trim()) return "";
        if ((value.match(/@/g) || []).length !== 1)
          return "Email must contain exactly one '@' symbol.";
        if (!/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/.test(value.trim()))
          return "Enter a valid email address (e.g. name@domain.com).";
        if (value.trim().length > 254)
          return "Email address is too long.";
        return "";
      }
      case "phone": {
        if (!value.trim()) return "";
        const digits = value.replace(/[\s\-]/g, "");
        if (!/^\d+$/.test(digits))
          return "Phone number must contain digits only — no letters or special characters.";
        if (digits.length !== 10)
          return "Phone number must be exactly 10 digits.";
        if (!/^[6-9]/.test(digits))
          return "Phone number must start with 6, 7, 8, or 9.";
        return "";
      }
      case "websiteUrl": {
        if (!value.trim()) return "";
        if (!/^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/.*)?$/.test(value.trim()))
          return "Enter a valid website URL (e.g. https://example.com).";
        return "";
      }
      case "linkedInUrl": {
        if (!value.trim()) return "";
        if (!/^(https?:\/\/)?(www\.)?linkedin\.com\/.+$/.test(value.trim()))
          return "Enter a valid LinkedIn URL (e.g. https://linkedin.com/company/name).";
        return "";
      }
      case "taxIdNumber": {
        if (!value.trim()) return "";
        if (value.trim().length < 3)   return "Tax ID must be at least 3 characters.";
        if (value.trim().length > 30)  return "Tax ID must not exceed 30 characters.";
        if (!/^[A-Z0-9\-\/]+$/i.test(value.trim()))
          return "Tax ID can only contain letters, numbers, hyphens, or slashes.";
        return "";
      }
      default:
        return "";
    }
  };

  const validateAll = () => {
    const newErrors = {
      companyName: validateField("companyName", formData.companyName),
      email:       validateField("email",       formData.email),
      phone:       validateField("phone",       formData.phone),
      websiteUrl:  validateField("websiteUrl",  formData.websiteUrl),
      linkedInUrl: validateField("linkedInUrl", formData.linkedInUrl),
      taxIdNumber: validateField("taxIdNumber", formData.taxIdNumber),
    };
    const filtered = Object.fromEntries(Object.entries(newErrors).filter(([, v]) => v !== ""));
    setErrors(filtered);
    return Object.keys(filtered).length === 0;
  };

  /* ===============================
     ERROR → SECTION MAP
     =============================== */
  const getErrorSection = (errorKeys) => {
    if (errorKeys.some(k => ["companyName","websiteUrl","linkedInUrl"].includes(k)))
      return "Company Info";
    if (errorKeys.some(k => ["email","phone","primaryContact"].includes(k)))
      return "Contact";
    if (errorKeys.includes("taxIdNumber"))
      return "Tax & Payment";
    return null;
  };

  /* ===============================
     HANDLERS
     =============================== */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val = type === "checkbox" ? checked : value;
    setFormData(prev => ({ ...prev, [name]: val }));
    if (touched[name]) {
      const err = validateField(name, val);
      setErrors(prev => err ? { ...prev, [name]: err } : (({ [name]: _, ...rest }) => rest)(prev));
    }
  };

  const handlePhoneChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d]/g, "").slice(0, 10);
    setFormData(prev => ({ ...prev, phone: cleaned }));
    if (touched.phone) {
      const err = validateField("phone", cleaned);
      setErrors(prev => err ? { ...prev, phone: err } : (({ phone: _, ...rest }) => rest)(prev));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setErrors(prev => err ? { ...prev, [name]: err } : (({ [name]: _, ...rest }) => rest)(prev));
  };

  const handleAddressChange = (addressType, field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [addressType]: { ...prev[addressType], [field]: value } };
      if (addressType === "billingAddress" && sameAsBilling) {
        updated.shippingAddress = { ...updated.billingAddress };
      }
      return updated;
    });
  };

  const handleSameAsBilling = (e) => {
    const checked = e.target.checked;
    setSameAsBilling(checked);
    setFormData(prev => ({
      ...prev,
      sameAsBilling: checked,
      shippingAddress: checked
        ? { ...prev.billingAddress }
        : { streetLine1: "", streetLine2: "", city: "", state: "", country: "", zipcode: "" },
    }));
  };

  /* ===============================
     SUBMIT — full 409 / duplicate handling
     =============================== */
  const handleSubmit = async () => {
    setTouched({ companyName: true, email: true, phone: true, websiteUrl: true, linkedInUrl: true, taxIdNumber: true });

    if (!validateAll()) {
      const errSection = getErrorSection(Object.keys(errors));
      if (errSection) setActiveSection(errSection);
      toast.warning("Please fix the highlighted errors before submitting.");
      return;
    }

    setLoading(true);
    try {
      const cleanedData = { ...formData };
      const enumFields = ['industry','relationshipType','companyStage','legalType','employeeCount',
        'annualRevenue','leadStatus','leadSource','taxIdType','paymentTerms','currency'];
      enumFields.forEach(field => { if (cleanedData[field] === "") cleanedData[field] = null; });

      const payload = {
        ...cleanedData,
        email:      formData.email.trim().toLowerCase() || null,
        phone:      formData.phone.trim() || null,
        companyName: formData.companyName.trim(),
        contactIds: formData.contactIds.map(c => c.id ?? c.value),
      };

      await companiesAPI.addCompany(payload);
      toast.success("Company created successfully!");
      onCreate?.();
      onClose();

    } catch (error) {
      const status  = error.response?.status;
      const message = error.response?.data?.message || "";
      const msgLower = message.toLowerCase();

      // ── 409 Conflict — server confirmed duplicate ──────────────────────────
      if (status === 409) {
        if (msgLower.includes("name")) {
          setErrors(prev => ({ ...prev, companyName: "A company with this name already exists in your organization." }));
          setActiveSection("Company Info");
          toast.error("Company name already exists. Please use a different name.");

        } else if (msgLower.includes("email")) {
          setErrors(prev => ({ ...prev, email: "This email is already associated with another company." }));
          setActiveSection("Contact");
          toast.error("This email address is already in use by another company.");

        } else if (msgLower.includes("phone")) {
          setErrors(prev => ({ ...prev, phone: "This phone number is already associated with another company." }));
          setActiveSection("Contact");
          toast.error("This phone number is already in use by another company.");

        } else if (msgLower.includes("primary_contact") || msgLower.includes("primarycontact")) {
          setErrors(prev => ({ ...prev, primaryContact: "This contact is already assigned as primary contact to another company." }));
          setActiveSection("Contact");
          toast.error("Primary contact is already linked to another company.");

        } else {
          toast.error("A company with the same details already exists. Please check and try again.");
        }
        return;
      }

      // ── 400 Bad Request ────────────────────────────────────────────────────
      if (status === 400) {
        // Backend may still send duplicate messages with 400 — handle gracefully
        if (msgLower.includes("name") && (msgLower.includes("exists") || msgLower.includes("duplicate"))) {
          setErrors(prev => ({ ...prev, companyName: "A company with this name already exists in your organization." }));
          setActiveSection("Company Info");
          toast.error("Company name already exists.");
        } else if (msgLower.includes("email") && (msgLower.includes("exists") || msgLower.includes("duplicate"))) {
          setErrors(prev => ({ ...prev, email: "This email is already associated with another company." }));
          setActiveSection("Contact");
          toast.error("This email is already in use.");
        } else {
          toast.error(message || "Invalid data submitted. Please review all fields and try again.");
        }
        return;
      }

      // ── Other HTTP errors ──────────────────────────────────────────────────
      if (status === 403) {
        toast.error("You don't have permission to create companies.");
      } else if (status === 500) {
        toast.error("Server error. Please try again in a moment.");
      } else {
        toast.error(message || "Failed to create company. Please try again.");
      }

    } finally {
      setLoading(false);
    }
  };

  /* ===============================
     UI HELPERS
     =============================== */
  const fieldCls = (name) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent bg-white transition-colors ${
      errors[name] ? "border-red-400 bg-red-50" : "border-gray-300"
    }`;

  const selectCls = `w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none 
    focus:ring-2 focus:ring-[#1C4E6D] focus:border-transparent bg-white transition-colors appearance-none`;

  const labelCls  = "block text-sm font-medium text-gray-700 mb-1";
  const fieldWrap = "flex flex-col gap-1";

  const ErrorMsg = ({ field }) =>
    errors[field] ? (
      <p className="text-red-600 text-xs mt-1 flex items-center gap-1">
        <span>⚠</span> {errors[field]}
      </p>
    ) : null;

  const sectionHasError = (section) => {
    const map = {
      "Company Info":  ["companyName", "websiteUrl", "linkedInUrl"],
      "Contact":       ["email", "phone", "primaryContact"],
      "Tax & Payment": ["taxIdNumber"],
    };
    return (map[section] || []).some(f => errors[f]);
  };

  /* ===============================
     ADDRESS COMPONENT
     =============================== */
  const AddressFields = ({ type, label }) => (
    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{label}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={fieldWrap + " md:col-span-2"}>
          <label className={labelCls}>Street Line 1</label>
          <input className={selectCls} placeholder="123 Main St" value={formData[type].streetLine1}
            onChange={(e) => handleAddressChange(type, "streetLine1", e.target.value)}
            disabled={type === "shippingAddress" && sameAsBilling} />
        </div>
        <div className={fieldWrap + " md:col-span-2"}>
          <label className={labelCls}>Street Line 2</label>
          <input className={selectCls} placeholder="Suite, Floor, etc." value={formData[type].streetLine2}
            onChange={(e) => handleAddressChange(type, "streetLine2", e.target.value)}
            disabled={type === "shippingAddress" && sameAsBilling} />
        </div>
        {[["city","City"],["state","State"],["country","Country"],["zipcode","Zipcode"]].map(([field, ph]) => (
          <div key={field} className={fieldWrap}>
            <label className={labelCls}>{ph}</label>
            <input className={selectCls} placeholder={ph} value={formData[type][field]}
              onChange={(e) => handleAddressChange(type, field, e.target.value)}
              disabled={type === "shippingAddress" && sameAsBilling} />
          </div>
        ))}
      </div>
    </div>
  );

  /* ===============================
     SECTION RENDERS
     =============================== */
  const renderSection = () => {
    switch (activeSection) {
      case "Company Info":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={fieldWrap + " md:col-span-2"}>
                <label className={labelCls}>Company Name <span className="text-red-500">*</span></label>
                <input className={fieldCls("companyName")} name="companyName"
                  value={formData.companyName} onChange={handleChange} onBlur={handleBlur}
                  placeholder="Acme Corporation" maxLength={100} />
                <ErrorMsg field="companyName" />
              </div>
              <div className={fieldWrap}>
                <label className={labelCls}>Website URL</label>
                <input className={fieldCls("websiteUrl")} name="websiteUrl"
                  value={formData.websiteUrl} onChange={handleChange} onBlur={handleBlur}
                  placeholder="https://example.com" />
                <ErrorMsg field="websiteUrl" />
              </div>
              <div className={fieldWrap}>
                <label className={labelCls}>LinkedIn URL</label>
                <input className={fieldCls("linkedInUrl")} name="linkedInUrl"
                  value={formData.linkedInUrl} onChange={handleChange} onBlur={handleBlur}
                  placeholder="https://linkedin.com/company/..." />
                <ErrorMsg field="linkedInUrl" />
              </div>
              {[
                { name: "industry", label: "Industry", options: ["IT","FINANCE","HEALTHCARE","EDUCATION","MANUFACTURING","RETAIL","OTHER"] },
                { name: "relationshipType", label: "Relationship Type", options: ["CLIENT","PARTNER","SUPPLIER","VENDOR","DISTRIBUTOR","RESELLER","CONTRACTOR","CONSULTANT","SERVICE_PROVIDER","INVESTOR","MANUFACTURER","OTHER"] },
                { name: "companyStage", label: "Company Stage", options: ["STARTUP","SMALL","MEDIUM","LARGE","ENTERPRISE","OTHER"] },
                { name: "legalType", label: "Legal Type", options: ["SOLE_PROPRIETORSHIP","PARTNERSHIP_FIRM","LLP","PRIVATE_LIMITED","PUBLIC_LIMITED","OPC","JOINT_VENTURE","NON_PROFIT","GOVERNMENT_ENTITY","OTHER"] },
              ].map(({ name, label, options }) => (
                <div key={name} className={fieldWrap}>
                  <label className={labelCls}>{label}</label>
                  <div className="relative">
                    <select className={selectCls} name={name} value={formData[name]} onChange={handleChange}>
                      <option value="">Select {label}</option>
                      {options.map(o => <option key={o} value={o}>{o.replace(/_/g," ")}</option>)}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              ))}
              <div className={fieldWrap}>
                <label className={labelCls}>Employee Count</label>
                <div className="relative">
                  <select className={selectCls} name="employeeCount" value={formData.employeeCount} onChange={handleChange}>
                    <option value="">Select Range</option>
                    {[["RANGE_1_10","1–10"],["RANGE_11_50","11–50"],["RANGE_51_100","51–100"],["RANGE_101_250","101–250"],["RANGE_251_500","251–500"],["RANGE_501_1000","501–1,000"],["RANGE_1001_5000","1,001–5,000"],["RANGE_5001_10000","5,001–10,000"],["RANGE_10000_PLUS","10,000+"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <div className={fieldWrap}>
                <label className={labelCls}>Annual Revenue</label>
                <div className="relative">
                  <select className={selectCls} name="annualRevenue" value={formData.annualRevenue} onChange={handleChange}>
                    <option value="">Select Revenue</option>
                    {[["BELOW_1M","Below $1M"],["RANGE_1M_5M","$1M–$5M"],["RANGE_5M_10M","$5M–$10M"],["RANGE_10M_50M","$10M–$50M"],["RANGE_50M_100M","$50M–$100M"],["RANGE_100M_500M","$100M–$500M"],["RANGE_500M_1B","$500M–$1B"],["RANGE_1B_5B","$1B–$5B"],["RANGE_5B_10B","$5B–$10B"],["ABOVE_10B","Above $10B"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" name="isShared" checked={formData.isShared} onChange={handleChange}
                  className="w-4 h-4 text-[#1C4E6D] rounded border-gray-300" />
                <span className="text-sm text-gray-600">
                  <Shield size={13} className="inline mr-1 text-blue-500" />
                  Share with all organization members (read-only)
                </span>
              </label>
            </div>
          </div>
        );

      case "Lead Details":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { name:"leadStatus", label:"Lead Status", options:["NEW","CONTACTED","QUALIFIED","LOST","WON","OTHER"] },
              { name:"leadSource", label:"Lead Source", options:["WEBSITE","REFERRAL","PARTNER","SOCIAL_MEDIA","EMAIL","CALL","OTHER"] },
            ].map(({ name, label, options }) => (
              <div key={name} className={fieldWrap}>
                <label className={labelCls}>{label}</label>
                <div className="relative">
                  <select className={selectCls} name={name} value={formData[name]} onChange={handleChange}>
                    <option value="">Select {label}</option>
                    {options.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
            ))}
          </div>
        );

      case "Contact":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={fieldWrap}>
                <label className={labelCls}>Primary Contact</label>
                <input className={fieldCls("primaryContact")} name="primaryContact"
                  value={formData.primaryContact} onChange={handleChange}
                  placeholder="Contact person name" />
                <ErrorMsg field="primaryContact" />
              </div>
              <div className={fieldWrap}>
                <label className={labelCls}>Associated Contacts</label>
                <Select isMulti options={contacts} value={formData.contactIds}
                  onChange={(selected) => setFormData(prev => ({ ...prev, contactIds: selected || [] }))}
                  isLoading={contactsLoading} placeholder="Select contacts"
                  className="text-sm" classNamePrefix="react-select" />
              </div>
              <div className={fieldWrap}>
                <label className={labelCls}>Job Title</label>
                <input className={fieldCls("jobTitle")} name="jobTitle"
                  value={formData.jobTitle} onChange={handleChange} placeholder="Job title" />
              </div>
              <div className={fieldWrap}>
                <label className={labelCls}>Email</label>
                <input className={fieldCls("email")} type="text" name="email"
                  value={formData.email} onChange={handleChange} onBlur={handleBlur}
                  placeholder="email@example.com" maxLength={254} />
                <ErrorMsg field="email" />
              </div>
              <div className={fieldWrap}>
                <label className={labelCls}>Phone</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500 select-none">+91</span>
                  <input
                    className={`${fieldCls("phone")} pl-12`}
                    type="tel" inputMode="numeric" name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    onBlur={(e) => {
                      setTouched(p => ({...p, phone: true}));
                      const err = validateField("phone", e.target.value);
                      setErrors(p => err ? {...p, phone: err} : (({phone:_,...r})=>r)(p));
                    }}
                    placeholder="9XXXXXXXXX" maxLength={10} />
                </div>
                <p className={`text-xs mt-1 text-right ${formData.phone.length === 10 ? "text-green-600" : "text-gray-400"}`}>
                  {formData.phone.length}/10
                </p>
                <ErrorMsg field="phone" />
              </div>
            </div>
            <div className="flex items-center gap-6 pt-2">
              {[["doNotCall","Do Not Call"],["doNotEmail","Do Not Email"]].map(([name, label]) => (
                <label key={name} className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" name={name} checked={formData[name]} onChange={handleChange}
                    className="w-4 h-4 text-red-500 rounded border-gray-300" />
                  <span className="text-sm text-gray-600">{label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case "Address":
        return (
          <div className="space-y-4">
            <AddressFields type="billingAddress" label="Billing Address" />
            <label className="flex items-center gap-2 cursor-pointer px-1">
              <input type="checkbox" checked={sameAsBilling} onChange={handleSameAsBilling}
                className="w-4 h-4 text-[#1C4E6D] rounded border-gray-300" />
              <span className="text-sm text-gray-600">Shipping address same as billing</span>
            </label>
            <AddressFields type="shippingAddress" label="Shipping Address" />
          </div>
        );

      case "Tax & Payment":
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={fieldWrap}>
              <label className={labelCls}>Tax ID Type</label>
              <div className="relative">
                <select className={selectCls} name="taxIdType" value={formData.taxIdType} onChange={handleChange}>
                  {["GST","EIN","VAT","TRN","TIN","ABN","CIF","CNPJ","TVA","IVA","BTW","CUIT","RFC","RUC","NIT","CPF","RIF","OTHER"].map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div className={fieldWrap}>
              <label className={labelCls}>Tax ID Number</label>
              <input className={fieldCls("taxIdNumber")} name="taxIdNumber"
                value={formData.taxIdNumber} onChange={handleChange} onBlur={handleBlur}
                placeholder="e.g. 29ABCDE1234F1Z5" maxLength={30}
                style={{ textTransform: "uppercase" }} />
              <ErrorMsg field="taxIdNumber" />
            </div>
            <div className={fieldWrap}>
              <label className={labelCls}>Payment Terms</label>
              <div className="relative">
                <select className={selectCls} name="paymentTerms" value={formData.paymentTerms} onChange={handleChange}>
                  <option value="">Select Terms</option>
                  {[["NET15","Net 15"],["NET30","Net 30"],["NET45","Net 45"],["NET60","Net 60"],["NET90","Net 90"],["COD","Cash on Delivery"],["PREPAID","Prepaid"],["LC","Letter of Credit"],["PAYMENT_ON_RECEIPT","Payment on Receipt"],["MONTHLY","Monthly"],["QUARTERLY","Quarterly"],["ANNUAL","Annual"],["OTHER","Other"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
            <div className={fieldWrap}>
              <label className={labelCls}>Currency</label>
              <div className="relative">
                <select className={selectCls} name="currency" value={formData.currency} onChange={handleChange}>
                  <option value="">Select Currency</option>
                  {["INR","USD","EUR","GBP","JPY","CNY","CAD","AUD","CHF","SEK","NOK","DKK","SGD","HKD","NZD","ZAR","BRL","MXN","AED","SAR","KRW","THB","MYR","PHP","IDR","VND","EGP","NGN","KES","OTHER"].map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            </div>
          </div>
        );

      default: return null;
    }
  };

  const sectionIcons = {
    "Company Info":  <Building2 size={14} />,
    "Lead Details":  <ChevronDown size={14} />,
    "Contact":       <Users size={14} />,
    "Address":       <MapPin size={14} />,
    "Tax & Payment": <CreditCard size={14} />,
  };

  /* ===============================
     RENDER
     =============================== */
  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="bg-white shadow-2xl w-full max-w-2xl h-full flex flex-col animate-slideInRight overflow-hidden">

        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Create Company</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-gray-100 overflow-x-auto flex-shrink-0 bg-white">
          {SECTIONS.map((section) => (
            <button key={section} onClick={() => setActiveSection(section)}
              className={`relative flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeSection === section
                  ? "border-[#1C4E6D] text-[#1C4E6D] bg-white"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}>
              {sectionIcons[section]}
              {section}
              {sectionHasError(section) && (
                <span className="absolute top-2 right-1 w-1.5 h-1.5 bg-red-500 rounded-full" />
              )}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {renderSection()}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t bg-gray-50 flex-shrink-0">
          <div className="flex gap-1">
            {SECTIONS.map((s) => (
              <div key={s} onClick={() => setActiveSection(s)}
                className={`w-2 h-2 rounded-full cursor-pointer transition-colors ${
                  activeSection === s ? "bg-[#1C4E6D]" : sectionHasError(s) ? "bg-red-400" : "bg-gray-300"
                }`} />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition font-medium">
              Cancel
            </button>
            <button onClick={handleSubmit} disabled={loading}
              className="px-5 py-2 text-sm rounded-lg bg-[#1C4E6D] text-white hover:bg-[#153b52] transition font-medium disabled:opacity-50 disabled:cursor-not-allowed">
              {loading ? "Creating..." : "Create Company"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
        .animate-slideInRight { animation: slideInRight 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default CreateCompanyModal;